import { AddressRecord, GooglePlacesSuggestionResult } from '@navio/nsdk';
import { BookingAddress } from 'noddi-types';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessage, NoddiInput, NoddiSpinner } from 'noddi-ui-web';
import { commonTexts, useDebounceValue } from 'noddi-util';
import { useEffect, useId, useRef, useState } from 'react';
import { getLongAddressName } from '../../utils/address';
import { renderAddressSuggestionOption, useAddressSuggestions } from './addressSuggestions';
import { shouldFetchPlaceSuggestions } from './savedAddressChoices';
import { AddressSearchTranslations } from './types';

type AddressSearchInlineProps = {
  onSelect: (address: GooglePlacesSuggestionResult) => void;
  /** Names of addresses the caller already shows. A match does not call Places. */
  savedAddressNames?: readonly string[];
  onClear?: () => void;
  translations?: AddressSearchTranslations;
  /** External work in flight (resolving the picked place), shown in the same slot as the search spinner. */
  loading: boolean;
  defaultValue?: { id: number; name: string } | AddressRecord | BookingAddress;
  countryCodes?: string[];
  startIcon?: NoddiIconName;
  label?: string;
};

/**
 * Address search as an in-flow input plus an in-flow suggestion list, instead of
 * the popover combobox in {@link AddressSearch}.
 *
 * A popover is anchored to its trigger, so inside a bottom sheet the phone
 * keyboard covers it and nothing moves it out of the way. A focused input in the
 * normal flow is scrolled into view by the browser, exactly like every other
 * field in the sheet — so this is the variant to use inside one.
 */
export const AddressSearchInline = ({
  onSelect,
  savedAddressNames = [],
  onClear,
  translations,
  loading,
  defaultValue,
  countryCodes,
  startIcon = 'MapPin',
  label
}: AddressSearchInlineProps) => {
  const safeTrans = translations ?? commonTexts.addressSearch;
  const defaultName = defaultValue
    ? 'name' in defaultValue
      ? defaultValue.name
      : getLongAddressName(defaultValue)
    : '';

  const listboxId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(defaultName);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // The keyboard resizes the visual viewport a moment after focus, and the browser's own
  // scroll-into-view runs before that — inside a fixed bottom sheet it leaves the field above
  // the visible strip, so the first tap has the customer typing blind. Re-anchor the field to
  // the top of the sheet's scroll area once the viewport has actually settled, which is what
  // dismissing the keyboard and tapping again does by hand.
  useEffect(() => {
    if (!isFocused) {
      return;
    }

    const anchorField = () => fieldRef.current?.scrollIntoView({ block: 'start' });
    const settleTimeout = window.setTimeout(anchorField, 350);
    window.visualViewport?.addEventListener('resize', anchorField);

    return () => {
      window.clearTimeout(settleTimeout);
      window.visualViewport?.removeEventListener('resize', anchorField);
    };
  }, [isFocused]);

  // Discard unresolved picks after loading, preserving a new search typed during cancellation.
  const [prevDefaultId, setPrevDefaultId] = useState(defaultValue?.id);
  const [wasLoading, setWasLoading] = useState(loading);
  if (defaultValue?.id !== prevDefaultId || loading !== wasLoading) {
    setPrevDefaultId(defaultValue?.id);
    setWasLoading(loading);
    if (defaultValue?.id !== prevDefaultId || (wasLoading && !loading && !isBrowsing)) {
      setQuery(defaultName);
      setIsBrowsing(false);
    }
  }

  const liveQuery = isBrowsing ? query : '';
  const fetchPlaces = shouldFetchPlaceSuggestions(liveQuery, savedAddressNames);
  const debouncedQuery = useDebounceValue({ value: fetchPlaces ? query : '', delay: 300 });
  const { addressSuggestionOptions, isAddressSuggestionsLoading, addressSuggestionsError } = useAddressSuggestions(
    debouncedQuery,
    countryCodes
  );

  const placeOptions = addressSuggestionOptions ?? [];
  const isListOpen = isBrowsing && fetchPlaces;
  const isSearching = fetchPlaces && (isAddressSuggestionsLoading || query !== debouncedQuery);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setIsBrowsing(true);
    setActiveIndex(0);
    // Typing invalidates whatever was picked before — drop it rather than saving a stale address.
    onClear?.();
  };

  const handleClear = () => {
    setQuery('');
    setIsBrowsing(false);
    setActiveIndex(0);
    onClear?.();
  };

  const handleSelect = (suggestion: GooglePlacesSuggestionResult | undefined) => {
    if (!suggestion) {
      return;
    }
    setQuery(suggestion.mainText);
    setIsBrowsing(false);
    onSelect(suggestion);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isListOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(placeOptions.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(placeOptions[activeIndex]?.originalData);
      return;
    }
    if (event.key === 'Escape') {
      setIsBrowsing(false);
    }
  };

  return (
    <div ref={fieldRef} className='flex scroll-mt-4 flex-col gap-1'>
      <NoddiInput
        label={label}
        role='combobox'
        aria-expanded={isListOpen}
        aria-controls={listboxId}
        aria-autocomplete='list'
        aria-activedescendant={isListOpen && placeOptions.length > 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete='off'
        placeholder={safeTrans.searchForAddress}
        value={query}
        maxLength={1024}
        startAdornment={<NoddiIcon name={startIcon} className='text-muted-foreground' />}
        endAdornment={
          isSearching || loading ? (
            <NoddiSpinner size='small' />
          ) : query.length > 0 ? (
            <button
              type='button'
              onClick={handleClear}
              aria-label={`Clear ${label ?? safeTrans.searchForAddress}`}
              className='flex items-center text-muted-foreground hover:text-foreground'
            >
              <NoddiIcon name='X' />
            </button>
          ) : undefined
        }
        onChange={(event) => handleQueryChange(event.target.value)}
        onFocus={() => {
          setIsBrowsing(true);
          setIsFocused(true);
        }}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
      />

      {addressSuggestionsError && <ApiErrorMessage error={addressSuggestionsError} />}

      {isListOpen && (
        <div
          id={listboxId}
          role='listbox'
          aria-label={safeTrans.searchForAddress}
          className='max-h-64 overflow-y-auto rounded-md border border-input bg-white shadow-xs'
        >
          {placeOptions.map((option, index) => (
            <button
              key={String(option.value)}
              id={`${listboxId}-option-${index}`}
              type='button'
              role='option'
              aria-selected={index === activeIndex}
              aria-label={option.label}
              // Arrow keys and Enter are handled on the input (aria-activedescendant), so the
              // rows stay out of the tab order.
              tabIndex={-1}
              // Keep focus on the input so the keyboard does not close and reopen under the tap.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option.originalData)}
              className={cn(
                'block w-full border-b border-border p-3 text-left last:border-b-0',
                index === activeIndex && 'bg-accent'
              )}
            >
              {renderAddressSuggestionOption(option)}
            </button>
          ))}

          {placeOptions.length === 0 &&
            (isSearching ? (
              <div className='flex justify-center py-4'>
                <NoddiSpinner size='small' />
              </div>
            ) : (
              <div className='px-3 py-4 text-sm text-muted-foreground'>{safeTrans.noAddressesFound}</div>
            ))}
        </div>
      )}
    </div>
  );
};
